import React from "react";

import { IconDefinition, IconProp } from "@fortawesome/fontawesome-svg-core";
import { Link } from "react-router-dom";
import { twJoin } from "tailwind-merge";

import { Button } from "src/base-components/Button";
import { Icon } from "src/design-system/Icon";

const VARIANTS = {
  default: "bg-indigo-100",
  error: "bg-red-100",
} as const;

const SIZES = {
  default: "py-16",
  compact: "py-4",
} as const;

export type ActionButtonProps = {
  label: string;
  icon?: IconDefinition;
  isDisabled?: boolean;
} & (
  | {
      onClick: () => void;
      linkTo?: never;
    }
  | {
      linkTo: string;
      onClick?: never;
    }
);

export type EmptyProps = {
  dataLoc?: string;
  icon: IconProp;
  headline: React.ReactNode;
  description: React.ReactNode;
  action?: React.ReactNode;
  variant?: keyof typeof VARIANTS;
  size?: keyof typeof SIZES;
  actionButton?: ActionButtonProps | false;
};

export const EmptyState: React.FC<EmptyProps> = ({
  dataLoc,
  icon,
  headline,
  description,
  action,
  actionButton,
  variant = "default",
  size = "default",
}) => (
  <div
    className={twJoin(
      "flex h-full flex-col items-center justify-center text-center",
      SIZES[size],
    )}
    data-loc={dataLoc}
  >
    <div
      aria-hidden="true"
      className={twJoin("mb-4 inline-block rounded-lg p-3", VARIANTS[variant])}
    >
      <Icon
        icon={icon}
        size="md"
        variant={variant === "error" ? "error" : "active"}
      />
    </div>
    <h2
      className="mb-2 inline-block max-w-60 font-inter-semibold-13px text-gray-800"
      data-record="true"
    >
      {headline}
    </h2>
    <p
      className={twJoin(
        "inline-block min-h-0 max-w-60 overflow-hidden break-words font-inter-normal-12px text-gray-500",
        (action || actionButton) && "mb-6",
      )}
    >
      {description}
    </p>
    {actionButton &&
      (actionButton.linkTo ? (
        <Link to={actionButton.linkTo}>
          <Button
            dataLoc={`${dataLoc}-action`}
            disabled={actionButton.isDisabled}
            iconLeft={actionButton.icon}
            variant="secondary"
          >
            {actionButton.label}
          </Button>
        </Link>
      ) : (
        <Button
          dataLoc={`${dataLoc}-action`}
          disabled={actionButton.isDisabled}
          iconLeft={actionButton.icon}
          variant="secondary"
          onClick={actionButton.onClick}
        >
          {actionButton.label}
        </Button>
      ))}
    {action}
  </div>
);
